import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { MatDialog } from '@angular/material/dialog';
import {
  NavigationCancel,
  NavigationError,
  NavigationStart,
  Router,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
} from '@angular/router';
import { filter } from 'rxjs';

import { HttpLoadingStateService } from '@core/services/http-loading-state.service';
import { AuthFacadeService } from '@features/auth/services/auth-facade.service';
import { DashboardFacadeService } from '@features/dashboard/services/dashboard-facade.service';
import { MyAccountFacadeService } from '@features/my-account/services/my-account-facade.service';
import {
  AuthorizationFaceRecognitionDialogComponent,
  FACE_LIVENESS_DIALOG_CONFIG,
  FaceRecognitionDialogData,
  FaceRecognitionDialogResult,
} from '@shared/components/authorization-face-recognition-dialog/authorization-face-recognition-dialog.component';
import { ShellStateService } from '@layout/state/shell-state.service';

/** localStorage key under which the rail's expanded/collapsed choice is persisted. */
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'veredis.shell.sidebar-collapsed';

/**
 * Below this width the rail always starts collapsed.
 *
 * 81.25rem = 1300px, mirroring `--breakpoint-rail` in `src/tailwind.css` - the
 * token is the home of the number and the spec below fails if these two drift.
 * The rail is 350px wide at every width, which is 29.5% of a 1267px viewport
 * against 19.5% of a 1920px one - so the narrow screens pay the most for it and
 * have the least to give. Collapsing hands ~263px back to the content, which is
 * why the dashboard's own grids read their density off their container rather
 * than off the window.
 *
 * Written in `rem` for the same reason the `@theme` note gives: the breakpoint
 * tokens are in `rem`, and a `px` number here would drift from the layout the
 * moment the root font size is not 16.
 */
export const SIDEBAR_AUTO_COLLAPSE_QUERY = '(width < 81.25rem)';

/**
 * Desktop shell mode: rail pushes `main`. Mirrors `--breakpoint-desktop`
 * (`79.1875rem` = 1267px) and its `>=` cut, so the shell and every `desktop:` /
 * `max-desktop:` variant in the pages flip at the same width.
 */
export const SHELL_MODE_DESKTOP_QUERY = '(width >= 79.1875rem)';

/** Mobile shell mode: rail leaves the flow. Mirrors `--breakpoint-mobile` (`48rem`). */
export const SHELL_MODE_MOBILE_QUERY = '(width < 48rem)';

export type ShellMode = 'desktop' | 'tablet' | 'mobile';

function mediaQuery(query: string): MediaQueryList | null {
  return typeof globalThis.matchMedia === 'function' ? globalThis.matchMedia(query) : null;
}

@Component({
  selector: 'app-dashboard-layout',
  standalone: false,
  templateUrl: './dashboard-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // `h-screen overflow-hidden` is what makes `main`'s own `overflow-y-auto`
    // the scroller. Under `min-h-screen` the shell simply grew past the viewport
    // and the window scrolled instead, taking the rail and the page header with
    // it - 932px of scroll at 1366x768, so the navigation left the screen after
    // one flick of the wheel.
    class: 'relative flex w-full h-screen items-stretch overflow-hidden font-[Manrope,sans-serif]',
    '[class.shell-mode-desktop]': 'shellMode() === "desktop"',
    '[class.shell-mode-tablet]': 'shellMode() === "tablet"',
    '[class.shell-mode-mobile]': 'shellMode() === "mobile"',
    '(document:keydown.escape)': 'onEscape($event)',
  },
})
export class DashboardLayoutComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly httpLoading = inject(HttpLoadingStateService);
  private readonly shellState = inject(ShellStateService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authFacade = inject(AuthFacadeService);
  private readonly myAccountFacade = inject(MyAccountFacadeService);
  private readonly dashboardFacade = inject(DashboardFacadeService);
  private readonly focusTrapFactory = inject(FocusTrapFactory);
  private readonly injector = inject(Injector);

  @ViewChild('navPanel') private navPanel?: ElementRef<HTMLElement>;
  @ViewChild('mobileMenuTrigger') private mobileMenuTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('tabletExpandTrigger') private tabletExpandTrigger?: ElementRef<HTMLButtonElement>;

  /**
   * The shell media queries, or `null` where `matchMedia` is not implemented -
   * jsdom under the unit tests, which has no viewport to follow. The rail then
   * stays where it was, expanded, and the shell is the desktop one.
   */
  private readonly autoCollapseQuery = mediaQuery(SIDEBAR_AUTO_COLLAPSE_QUERY);
  private readonly shellDesktopQuery = mediaQuery(SHELL_MODE_DESKTOP_QUERY);
  private readonly shellMobileQuery = mediaQuery(SHELL_MODE_MOBILE_QUERY);

  private readonly _collapsed = signal(this.initialCollapsed());
  private readonly _drawerOpen = signal(false);
  private readonly _shellMode = signal<ShellMode>(this.resolveShellMode());

  /**
   * Whether the facial-recognition gate is on screen: until the session's user
   * carries `face: true`. A payload without the flag counts as not enrolled.
   */
  private readonly _facialEnrollmentOpen = signal(this.authFacade.vm().user?.face !== true);
  private routeConfigLoadDepth = 0;

  /**
   * Whether the rail is still following the viewport. It stops the moment the
   * user touches the toggle: an automatic width rule that keeps overriding a
   * deliberate choice is worse than no rule at all.
   */
  private followsViewport = this.readPersistedCollapsed() === null;

  private focusTrap: FocusTrap | null = null;
  private lastOverlayTrigger: HTMLElement | null = null;

  readonly collapsed = this._collapsed.asReadonly();
  readonly drawerOpen = this._drawerOpen.asReadonly();
  readonly shellMode = this._shellMode.asReadonly();
  readonly facialEnrollmentOpen = this._facialEnrollmentOpen.asReadonly();

  readonly userName = 'Taylor Oliveira';
  readonly userRole = 'Analista';

  readonly navPanelId = 'shell-nav-panel';

  /** Whether the overlay behaves as a modal (tablet expand panel or mobile drawer). */
  readonly overlayModalActive = computed(() => this.drawerOpen() && this.shellMode() !== 'desktop');

  /** Drives label visibility inside the single `app-menu` instance. */
  readonly menuCollapsed = computed(() => {
    const mode = this.shellMode();
    if (mode === 'desktop') {
      return this.collapsed();
    }
    if (mode === 'tablet') {
      return !this.drawerOpen();
    }
    return false;
  });

  readonly toggleLabel = computed(() => {
    if (this.shellMode() === 'tablet') {
      return this.drawerOpen() ? 'NAV.CLOSE' : 'NAV.EXPAND';
    }

    return this.collapsed() ? 'NAV.EXPAND' : 'NAV.COLLAPSE';
  });

  /** Toggle artwork: the open rail shows the "close" mark, the collapsed one the "open" mark. */
  readonly toggleIconSrc = computed(() =>
    this.menuCollapsed() ? 'assets/icons/menu-close.png' : 'assets/icons/menu-open.png',
  );

  readonly tabletExpandExpanded = computed(
    () => this.shellMode() === 'tablet' && this.drawerOpen(),
  );

  ngOnInit(): void {
    this.watchViewportWidth();
    this.watchShellMode();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.closeDrawer();
        // Each route owns its trail; drop the previous page's so it cannot leak.
        this.shellState.setBreadcrumb(null);
      });

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof RouteConfigLoadStart ||
            event instanceof RouteConfigLoadEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event instanceof RouteConfigLoadStart) {
          this.routeConfigLoadDepth += 1;
          this.httpLoading.begin();
          return;
        }

        if (event instanceof RouteConfigLoadEnd) {
          if (this.routeConfigLoadDepth === 0) {
            return;
          }

          this.routeConfigLoadDepth -= 1;
          this.httpLoading.end();
          return;
        }

        while (this.routeConfigLoadDepth > 0) {
          this.routeConfigLoadDepth -= 1;
          this.httpLoading.end();
        }
      });

    this.destroyRef.onDestroy(() => {
      this.releaseFocusTrap();
    });
  }

  onEscape(event: Event): void {
    if (!this.overlayModalActive()) {
      return;
    }

    event.preventDefault();
    this.closeDrawer();
  }

  toggleSidebar(trigger?: HTMLElement): void {
    const mode = this.shellMode();

    if (mode === 'tablet') {
      if (this.drawerOpen()) {
        this.closeDrawer();
      } else {
        this.openDrawer(trigger ?? this.tabletExpandTrigger?.nativeElement ?? null);
      }
      return;
    }

    if (mode !== 'desktop') {
      return;
    }

    this._collapsed.update((v) => !v);
    // From here the rail is the user's, not the viewport's.
    this.followsViewport = false;

    // Below the auto-collapse width the choice lasts the session only. Startup
    // always collapses down here, so a write would never be read back on this
    // device - and it would bury the choice the user made on a wide screen,
    // which is the one reading it is for.
    if (this.autoCollapseQuery?.matches) {
      return;
    }

    this.persistCollapsed(this._collapsed());
  }

  /**
   * Hands the gate over to the facial-validation dialog.
   *
   * The gate stands down for the duration rather than sitting behind the dialog:
   * it is a `<dialog>` opened with `showModal()`, so it lives in the top layer
   * and paints over the CDK overlay whatever z-index that overlay carries - the
   * capture would open invisible and unclickable underneath it. A dismissed
   * capture puts the gate straight back, since the precondition is unmet until
   * the analyst confirms.
   *
   * The dialog's `decision` is part of its contract but nothing in its template
   * reads it, so the enrolment pass sends `AUTHORIZED` to satisfy the type.
   *
   * `liveness` is the enrolment flow's own: the dialog runs the AWS Face
   * Liveness challenge against the enrolment routes and only resolves confirmed
   * once the API has stored the face. Every caller opens it at the same 600x616;
   * the authorization screens get the plain webcam preview in it.
   */
  startFacialEnrollment(): void {
    this._facialEnrollmentOpen.set(false);

    this.dialog
      .open<
        AuthorizationFaceRecognitionDialogComponent,
        FaceRecognitionDialogData,
        FaceRecognitionDialogResult
      >(AuthorizationFaceRecognitionDialogComponent, {
        ...FACE_LIVENESS_DIALOG_CONFIG,
        data: {
          decision: 'AUTHORIZED',
          liveness: {
            openSession: () => this.authFacade.openFaceEnrollmentSession(),
            complete: (sessionId) => this.authFacade.enrollFace(sessionId),
          },
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.confirmed) {
          this._facialEnrollmentOpen.set(true);
          return;
        }

        this.reloadAfterFacialEnrollment();
      });
  }

  /**
   * Re-reads `user/profile` and `dashboard` once the face is stored: both were
   * read before enrolment and are stale from here. Each facade records its own
   * failure in its state, so the errors are swallowed here.
   */
  private reloadAfterFacialEnrollment(): void {
    this.myAccountFacade
      .loadProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });

    this.dashboardFacade
      .loadDashboard()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });
  }

  toggleDrawer(): void {
    if (this.drawerOpen()) {
      this.closeDrawer();
      return;
    }

    this.openDrawer(this.mobileMenuTrigger?.nativeElement ?? null);
  }

  openDrawer(trigger: HTMLElement | null): void {
    if (this.shellMode() === 'desktop' || this._drawerOpen()) {
      return;
    }

    this.lastOverlayTrigger = trigger;
    this._drawerOpen.set(true);
    this.installFocusTrap();
  }

  closeDrawer(): void {
    if (!this._drawerOpen()) {
      return;
    }

    this._drawerOpen.set(false);
    this.releaseFocusTrap();
    this.restoreOverlayFocus();
  }

  private installFocusTrap(): void {
    // After the render that lifts `invisible` off the drawer: a microtask runs
    // before it, and an element under `visibility: hidden` refuses focus.
    afterNextRender(
      {
        write: () => {
          const panel = this.navPanel?.nativeElement;
          if (!panel || !this.drawerOpen()) {
            return;
          }

          this.releaseFocusTrap();
          // Anchors attached now, not deferred: they are what wraps Tab at the ends of
          // the panel. Without them only `inert` holds focus, and WebKit - whose Tab
          // skips links by default - walks straight out past the close button.
          this.focusTrap = this.focusTrapFactory.create(panel);
          this.focusTrap.focusInitialElement();
        },
      },
      { injector: this.injector },
    );
  }

  private releaseFocusTrap(): void {
    this.focusTrap?.destroy();
    this.focusTrap = null;
  }

  private restoreOverlayFocus(): void {
    const trigger =
      this.lastOverlayTrigger ??
      (this.shellMode() === 'mobile'
        ? this.mobileMenuTrigger?.nativeElement
        : this.tabletExpandTrigger?.nativeElement) ??
      null;

    this.lastOverlayTrigger = null;

    // After the render that drops `inert` from the content column: the mobile
    // trigger lives there, and an inert element silently refuses focus.
    afterNextRender({ read: () => trigger?.focus() }, { injector: this.injector });
  }

  /**
   * Keeps the rail in step with the viewport until the user takes it over.
   *
   * `matchMedia` rather than a resize handler: the state only has two values and
   * only changes on one threshold, so a listener that fires per pixel of drag
   * would recompute nothing 99 times out of 100.
   */
  private watchViewportWidth(): void {
    const query = this.autoCollapseQuery;

    if (!query) {
      return;
    }

    const onChange = (event: MediaQueryListEvent): void => {
      if (this.followsViewport) {
        this._collapsed.set(event.matches);
      }
    };

    query.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }

  private watchShellMode(): void {
    const mobileQuery = this.shellMobileQuery;
    const desktopQuery = this.shellDesktopQuery;

    if (!mobileQuery || !desktopQuery) {
      return;
    }

    const onChange = (): void => {
      const next = this.resolveShellMode();
      if (next === this._shellMode()) {
        return;
      }

      const wasOpen = this._drawerOpen();
      if (wasOpen) {
        // Close without refocusing the old band's trigger, which is about to hide.
        this._drawerOpen.set(false);
        this.releaseFocusTrap();
        this.lastOverlayTrigger = null;
      }
      this._shellMode.set(next);
      if (wasOpen) {
        this.restoreOverlayFocus();
      }
    };

    mobileQuery.addEventListener('change', onChange);
    desktopQuery.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => {
      mobileQuery.removeEventListener('change', onChange);
      desktopQuery.removeEventListener('change', onChange);
    });
  }

  private resolveShellMode(): ShellMode {
    if (!this.shellMobileQuery || !this.shellDesktopQuery) {
      return 'desktop';
    }

    if (this.shellMobileQuery.matches) {
      return 'mobile';
    }

    if (this.shellDesktopQuery.matches) {
      return 'desktop';
    }

    return 'tablet';
  }

  /**
   * Collapsed at startup. A viewport under the auto-collapse width always opens
   * collapsed, even for a user who last left the rail open on a wider screen;
   * above it the remembered choice applies, and the rail is open without one.
   */
  private initialCollapsed(): boolean {
    if (this.autoCollapseQuery?.matches) {
      return true;
    }

    return this.readPersistedCollapsed() ?? false;
  }

  /** The remembered choice, or `null` when the user has never made one. */
  private readPersistedCollapsed(): boolean | null {
    try {
      const raw = localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
      return raw === null ? null : raw === 'true';
    } catch {
      return null;
    }
  }

  private persistCollapsed(collapsed: boolean): void {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {
      // Storage unavailable (private mode / quota) - keep in-memory state only.
    }
  }
}
